<script setup lang="ts">
import { ref, computed, nextTick, watch, inject } from 'vue'
import { storeToRefs } from 'pinia'
import {
  BotMessageSquare,
  Send,
  FolderKanban,
} from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MdPreview } from 'md-editor-v3'
import 'md-editor-v3/lib/preview.css'
import { useCopilotStore } from '../store'
import type { useCopilotChat } from '../composables/useCopilotChat'
import TemplateSelector from './TemplateSelector.vue'
import type { CopilotTemplate } from '@/types'

const copilotStore = useCopilotStore()
const { projectContext, selectedItemIds } = storeToRefs(copilotStore)

const chat = inject<ReturnType<typeof useCopilotChat>>('copilotChat')!
const {
  sessionId,
  messages,
  isSending,
  error,
  areProfilesLoading,
  selectedProfile,
  profileOptions,
  hasMessages,
  initSession,
  sendMessage,
  setSelectedProfile,
} = chat

const userInput = ref('')
const messagesContainer = ref<HTMLElement | null>(null)

const selectedCount = computed(() => selectedItemIds.value.length)

const hasProject = computed(() => !!projectContext.value)
const selectedProfileValue = computed<string>({
  get: () => selectedProfile.value || 'all',
  set: (value) => setSelectedProfile(value),
})
const selectedProfileLabel = computed(() => {
  return profileOptions.value.find((option) => option.value === selectedProfileValue.value)?.label
    || 'General (todas las tools)'
})

const scrollToBottom = async () => {
  await nextTick()
  if (messagesContainer.value) {
    const scrollEl = messagesContainer.value.querySelector('[data-radix-scroll-area-viewport]')
    if (scrollEl) {
      scrollEl.scrollTop = scrollEl.scrollHeight
    }
  }
}

watch(messages, () => {
  scrollToBottom()
}, { deep: true })

const handleSend = async () => {
  if (!userInput.value.trim() || !hasProject.value || isSending.value) return

  if (!sessionId.value) {
    initSession()
  }

  const context = copilotStore.buildContextPayload()
  if (!context) return

  const msg = userInput.value
  userInput.value = ''

  await sendMessage(msg, context)
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    handleSend()
  }
}

const handleTemplateSelect = async (template: CopilotTemplate) => {
  copilotStore.setTemplate(template)

  if (template.promptTemplate) {
    if (!sessionId.value) {
      initSession()
    }

    const context = copilotStore.buildContextPayload()
    if (!context) return

    userInput.value = ''
    await sendMessage(template.promptTemplate, {
      ...context,
      templatePrompt: template.promptTemplate,
    })
  }
}


</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Header -->
    <div class="border-b px-4 py-2.5 flex items-center justify-between shrink-0">
      <div class="flex items-center gap-2 min-w-0">
        <BotMessageSquare class="h-4 w-4" />
        <span class="text-sm font-medium">Chat</span>
        <Badge v-if="selectedCount > 0" variant="secondary" class="text-xs">
          {{ selectedCount }} items en contexto
        </Badge>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <Select v-model="selectedProfileValue" :disabled="areProfilesLoading">
          <SelectTrigger class="h-8 w-[190px] text-xs">
            <SelectValue placeholder="Rol">
              {{ selectedProfileLabel }}
            </SelectValue>
          </SelectTrigger>
          <SelectContent align="end" class="w-[230px]">
            <SelectItem
              v-for="option in profileOptions"
              :key="option.value"
              :value="option.value"
            >
              <div class="flex flex-col gap-0.5 py-0.5">
                <span class="text-sm leading-none">{{ option.label }}</span>
                <span class="text-[11px] leading-none text-muted-foreground">
                  {{ option.description }}
                </span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    <!-- Empty State: No Project -->
    <div
      v-if="!hasProject"
      class="flex-1 flex items-center justify-center p-8"
    >
      <div class="text-center space-y-3">
        <FolderKanban class="h-12 w-12 mx-auto text-muted-foreground/40" />
        <div>
          <p class="text-sm font-medium">Selecciona un proyecto</p>
          <p class="text-xs text-muted-foreground mt-1">
            Elige un proyecto en el panel izquierdo para comenzar
          </p>
        </div>
      </div>
    </div>

    <!-- Template Selector: No Messages Yet -->
    <div
      v-else-if="!hasMessages"
      class="flex-1 overflow-auto"
    >
      <div class="p-4 text-center space-y-2">
        <BotMessageSquare class="h-8 w-8 mx-auto text-muted-foreground/40" />
        <p class="text-sm font-medium">Elige un template o escribe tu consulta</p>
        <p class="text-xs text-muted-foreground">
          Los templates te ayudan a generar documentos especificos
        </p>
      </div>
      <TemplateSelector @select="handleTemplateSelect" />
    </div>

    <!-- Messages -->
    <div v-else class="flex-1 min-h-0" ref="messagesContainer">
      <ScrollArea class="h-full">
        <div class="p-4 space-y-4">
          <div
            v-for="message in messages"
            :key="message.id"
            class="flex gap-3"
            :class="{ 'justify-end': message.role === 'user' }"
          >
            <!-- Assistant message -->
            <div
              v-if="message.role === 'assistant'"
              class="flex gap-3 max-w-[85%]"
            >
              <div class="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <BotMessageSquare class="h-4 w-4 text-primary" />
              </div>
              <div class="bg-muted rounded-lg px-3 py-2 min-w-0">
                <MdPreview
                  :model-value="message.content"
                  language="en-US"
                  :theme="'light'"
                  class="copilot-preview"
                />
              </div>
            </div>

            <!-- User message -->
            <div
              v-else-if="message.role === 'user'"
              class="max-w-[75%]"
            >
              <div class="bg-primary text-primary-foreground rounded-lg px-3 py-2">
                <p class="text-sm whitespace-pre-wrap">{{ message.content }}</p>
              </div>
            </div>
          </div>

          <!-- Typing indicator -->
          <div v-if="isSending" class="flex gap-3">
            <div class="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <BotMessageSquare class="h-4 w-4 text-primary" />
            </div>
            <div class="bg-muted rounded-lg px-4 py-3">
              <div class="flex gap-1">
                <div class="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce" style="animation-delay: 0ms" />
                <div class="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce" style="animation-delay: 150ms" />
                <div class="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce" style="animation-delay: 300ms" />
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>

    <!-- Error -->
    <div v-if="error" class="px-4 py-2 bg-destructive/10 text-destructive text-xs">
      {{ error }}
    </div>

    <!-- Input Area -->
    <div v-if="hasProject" class="border-t p-3 space-y-2 shrink-0">
      <div class="flex gap-2">
        <Textarea
          v-model="userInput"
          placeholder="Escribe tu consulta al copiloto..."
          class="min-h-[40px] max-h-[120px] resize-none text-sm"
          rows="1"
          :disabled="isSending"
          @keydown="handleKeydown"
        />
        <Button
          size="icon"
          class="shrink-0 h-10 w-10"
          :disabled="!userInput.trim() || isSending"
          @click="handleSend"
        >
          <Send class="h-4 w-4" />
        </Button>
      </div>
      <p class="text-[10px] text-muted-foreground">
        Enter para enviar, Shift+Enter nueva linea
      </p>
    </div>
  </div>
</template>

<style>
.copilot-preview .md-editor-preview-wrapper {
  padding: 0 !important;
}
.copilot-preview .md-editor-preview {
  font-size: 0.875rem;
  line-height: 1.5;
}
.copilot-preview .md-editor-preview h1 {
  font-size: 1.25rem;
  margin-top: 1rem;
}
.copilot-preview .md-editor-preview h2 {
  font-size: 1.125rem;
  margin-top: 0.75rem;
}
.copilot-preview .md-editor-preview h3 {
  font-size: 1rem;
  margin-top: 0.5rem;
}
.copilot-preview .md-editor-preview p {
  margin-bottom: 0.5rem;
}
.copilot-preview .md-editor-preview ul,
.copilot-preview .md-editor-preview ol {
  padding-left: 1.25rem;
  margin-bottom: 0.5rem;
}
</style>
